import axios from 'axios';
import { getAccessToken } from 'client/utils/auth/msalHelpers';
import { apiBaseUrl } from 'client/utils/constants/frontEndConfig';
import { v4 as uuid } from 'uuid';

const REQUEST_ID_HEADER_KEY = 'X-Request-ID';
const config = {
    baseURL: apiBaseUrl,
    timeout: 60000
};

/**
 * Setups an interceptor with an access token and a unique request ID.
 * @param req
 * @returns {Promise<*>}
 */
const addAuthAndRequestHeaders = async (req) => {
    // Set the Authorization header
    req.headers.Authorization = await getAccessToken();
    // Set a unique request ID
    req.headers[REQUEST_ID_HEADER_KEY] = req?.headers[REQUEST_ID_HEADER_KEY] || uuid();
    return req;
};

/**
 * Sets the requestId to the provided. Useful for grouping api calls.
 * @param headers current list of headers e.g. ClientWithAuth.defaults.headers
 * @param requestId uuid
 * @returns {*&{"X-Request-ID": *}}
 */
const setCustomRequestId = (headers, requestId) => {
    return {
        ...headers,
        'X-Request-ID': requestId
    };
};

/**
 * Creating a singleton Axios instance to consume in all the other services.
 * Configured to use the base API URL for this instance with a 1 minute timeout
 * on all requests.
 */
const ClientWithAuth = axios.create(config);
const ClientWithoutAuth = axios.create(config);

const defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
};

/**
 * Adding default headers indicating all requests are expected to be
 * application/JSON.
 */
ClientWithAuth.defaults.headers = defaultHeaders;
ClientWithoutAuth.defaults.headers = defaultHeaders;

/**
 * Creating a callback function to set an access token as the Authorization header.
 * MSAL caches an access token and silently refreshes it if it has expired.
 * @param {Object} req - The intercepted request object.
 * @returns {Object} The modified request object which (if successful) includes the
 * Authorization header token.
 */

/**
 * Generic error handler for catching failed interceptions.
 * @param {Object} req - The intercepted request object.
 * @returns {Promise} A rejected promise with a status code and message
 */
const handleError = (err) => {
    // Server responded with code != 2xx, pass response error as-is
    if (err?.response) {
        if (err?.response?.data) {
            return Promise.reject(err.response.data);
        }
        // No error message from remote service, default to Axios code & message
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
            statusCode: err?.response?.status,
            message: err?.response?.statusText
        });
    }
    // Server not reached, use default error
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
        statusCode: 503,
        message: 'Service Unavailable'
    });
};

// Applying the interceptor functions on the Axios instance.
ClientWithAuth.interceptors.request.use(addAuthAndRequestHeaders, handleError);
ClientWithAuth.interceptors.response.use(res => res, handleError);
ClientWithoutAuth.interceptors.response.use(res => res, handleError);

export {
    ClientWithAuth,
    ClientWithoutAuth,
    setCustomRequestId
};
